// src/games/Quiz/index.tsx
import { message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import quizIcon from '../../assets/icons/quiz-icon.png';
import { GameHeader } from '../../components/GameHeader';
import { useAuth } from '../../hooks/auth';
import { usePopup } from '../../hooks/popup';
import { FinishModal } from '../core/finish/FinishModal';
import { useGameSession } from '../core/session/useGameSession';
import { QuizLoad, finishQuiz, loadQuizGame, quizResult, saveQuizProgress } from './api';
import { POINTS_PER_ANSWER, QuizAnswer, answerQuestion, currentQuestionIndex, isQuizComplete, quizPoints } from './rules';
import { Sidebar } from './Sidebar';
import { Container, Content, GameContent } from './styles';

/**
 * Rota `/jogo/quiz/:id`. O `key` remonta a tela (e a sessão) quando o id muda com a rota
 * montada: sem isso, as respostas do quiz A seriam salvas e finalizadas no quiz B. A tela
 * recebe o id por prop, nunca do `useParams`.
 */
export const Quiz: React.FC = () => {
  const jogoId = Number(useParams().id);
  return <QuizScreen key={jogoId} jogoId={jogoId} />;
};

const QuizScreen = ({ jogoId }: { jogoId: number }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const { mountPopup } = usePopup();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState<QuizLoad | null>(null);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    let alive = true;
    loadQuizGame(jogoId, userId)
      .then((res) => {
        if (!alive) return;
        if (!res) {
          message.warning('Este quiz ainda não tem perguntas.');
          navigate('/');
          return;
        }
        setAnswers(res.answers);
        setLoaded(res);
      })
      .catch(() => {
        if (!alive) return;
        message.error('Não foi possível abrir o jogo. Tente de novo.');
        navigate('/');
      });
    return () => {
      alive = false;
    };
  }, [jogoId, userId, navigate]);

  const questions = useMemo(() => loaded?.questions ?? [], [loaded]);
  const total = questions.length;
  const index = currentQuestionIndex(answers, questions);
  const question = questions[Math.min(index, total - 1)];

  const session = useGameSession({
    ready: loaded !== null,
    savedSeconds: loaded?.savedSeconds ?? 0,
    alreadyFinished: loaded?.finished ?? null,
    onSaveProgress: (seconds) =>
      userId !== undefined ? saveQuizProgress({ userId, jogoId, answers: answersRef.current, seconds }) : undefined,
  });
  const { finish, isPlaying } = session;

  // Fim automático ao responder a última pergunta (com a pontuação JÁ atualizada).
  useEffect(() => {
    if (!loaded || !isPlaying || !isQuizComplete(answers, questions)) return;
    const final = answers;
    void finish(quizResult(questions, final), async (seconds) => {
      if (userId === undefined) return;
      await finishQuiz({ userId, jogoId, answers: final, seconds });
    });
  }, [answers, finish, isPlaying, jogoId, loaded, questions, userId]);

  const handleAnswer = useCallback(
    (alternativeIndex: number) => {
      if (!isPlaying || !question) return;
      setAnswers((current) => answerQuestion(current, question, alternativeIndex));
    },
    [isPlaying, question]
  );

  const askRestart = () =>
    mountPopup({
      title: 'Recomeçar o quiz?',
      message: 'Suas respostas serão apagadas.',
      type: 'atencao',
      buttons: [
        { buttonText: 'Recomeçar', buttonFunction: () => setAnswers([]) },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  // Enquanto carrega não há ações (nem "Ver resultado" sem resultado).
  const menuItems =
    session.status === 'loading'
      ? []
      : isPlaying
        ? [{ buttonText: 'Recomeçar', buttonFunctions: askRestart }]
        : [{ buttonText: 'Ver resultado', buttonFunctions: session.showResult }];
  const maxScore = total * POINTS_PER_ANSWER;
  const currentScore = session.result?.points ?? quizPoints(answers);

  return (
    <Container>
      <GameHeader type="QUIZ" menuItems={menuItems} currentScore={currentScore} maxScore={maxScore} seconds={session.seconds} />
      <Content>
        <GameContent>
          <span>
            <p>
              {Math.min(index + 1, total)}/{total}
            </p>
            <p>
              <img src={quizIcon} alt="Game Icon" />
              QUIZ
            </p>
          </span>

          <div>
            <main>
              <section className="LeftSide">
                {question?.image && <img src={question.image} alt="Imagem da pergunta" />}

                <span>
                  <h3>{question?.question}</h3>
                </span>
              </section>
            </main>

            <Sidebar
              maxScore={maxScore}
              currentScore={currentScore}
              seconds={session.seconds}
              quiz={question}
              canAnswer={isPlaying}
              onAnswer={handleAnswer}
              onRestart={isPlaying ? askRestart : undefined}
            />
          </div>
        </GameContent>
      </Content>

      <FinishModal
        visible={session.modalVisible}
        result={session.result}
        seconds={session.seconds}
        error={session.error}
        busy={session.status === 'finishing'}
        onRetry={session.retry}
        onClose={session.hideResult}
        onHome={() => navigate('/')}
      />
    </Container>
  );
};
